import { readFragment, ResultOf } from "gql.tada";
import { GetArticlePageBySlug } from "@queries/GetArticlePageBySlug";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "motion/react";
import { IconX } from "@hawkins/assets";
import { ThemeContextProvider } from "@components/providers/ThemeContextProvider";
import { RichText } from "./core/RichText";
import { Lenis, useLenis } from "lenis/react";
import { MediaItem } from "./MediaItem";
import { Link } from "./core/Link";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Button } from "./core/Button";
import { IconArrowRight } from "@hawkins/assets";
import { useEffect, useRef } from "react";
import { MarkdownLite } from "./MarkdownLite";

export const ArticleModal = ({
  articlePage,
  open,
}: {
  articlePage: NonNullable<
    ResultOf<typeof GetArticlePageBySlug>["articlePageCollection"]
  >["items"][number];
  open: boolean;
}) => {
  return (
    <AnimatePresence mode="wait">
      {open && articlePage && (
        <Dialog.Portal forceMount key={articlePage.slug}>
          <Dialog.Overlay asChild>
            <motion.div
              className="fixed inset-0 z-[200]"
              // just animating opacity gave some weird results when the overlay covered particular items
              initial={{ backgroundColor: "rgb(var(--brand-black) / 0)" }}
              animate={{ backgroundColor: "rgb(var(--brand-black) / 0.8)" }}
              exit={{ backgroundColor: "rgb(var(--brand-black) / 0)" }}
            />
          </Dialog.Overlay>
          <ThemeContextProvider
            theme={{
              palette: "red",
              background: "neutral-light",
              foreground: "neutral-dark",
            }}
          >
            <Dialog.Content>
              <Lenis className="fixed top-0 right-0 z-[200] h-full w-full dsk:w-2/3 overflow-auto flex justify-end [&>*]:w-full">
                <ArticleModalContent articlePage={articlePage} />
              </Lenis>
            </Dialog.Content>
          </ThemeContextProvider>
        </Dialog.Portal>
      )}
    </AnimatePresence>
  );
};

const ArticleModalContent = ({
  articlePage,
}: {
  articlePage: NonNullable<
    NonNullable<
      ResultOf<typeof GetArticlePageBySlug>["articlePageCollection"]
    >["items"][number]
  >;
}) => {
  const lenis = useLenis();
  const rootRef = useRef<HTMLDivElement>(null);

  // This fixes lenis breaking scrolling when images pop-in slowly
  useEffect(() => {
    const element = rootRef.current;
    if (!element || !lenis) return;

    const resizeObserver = new ResizeObserver(() => {
      lenis.resize();
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [lenis]);

  return (
    <div ref={rootRef} className="p-4 dsk:p-8">
      <motion.div
        className="bg-palette-current-background rounded-3xl p-4 flex flex-col w-full dsk:w-full"
        initial={{
          opacity: 0,
          scale: 0.5,
        }}
        animate={{
          opacity: 1,
          scale: 1,
        }}
        exit={{
          opacity: 0,
          scale: 0.5,
        }}
      >
        <div className="flex flex-row items-center justify-between">
          <Dialog.DialogTitle asChild>
            <p className="pl-4 text-body-small uppercase">
              <MarkdownLite>
                {articlePage.modalTitle ?? articlePage.title}
              </MarkdownLite>
            </p>
          </Dialog.DialogTitle>
          <Dialog.Close asChild>
            <button className="size-9 min-w-9 min-h-9 rounded-lg bg-palette-current-foreground flex items-center justify-center focus:outline-none hover:scale-110 focus-visible:scale-110 active:scale-90 transition-transform">
              <IconX className="text-palette-current-background" size="small" />
            </button>
          </Dialog.Close>
        </div>
        <div className="pt-24 dsk:pt-36 w-full mb-20">
          <div className="mx-2 dsk:mx-11">
            <h3 className="text-heading-3">{articlePage.title}</h3>
            {articlePage.subtitle && (
              <p className="text-body-regular pt-8">{articlePage.subtitle}</p>
            )}
          </div>
          {articlePage.heroImage && (
            <div className="rounded-xl overflow-hidden my-20">
              <MediaItem
                item={articlePage.heroImage}
                className="size-full object-cover"
                controls
                muted={false}
                autoPlay={false}
                width={1000}
              />
            </div>
          )}

          <div className="px-2 dsk:px-11 text-palette-current-foreground mt-20 [&_h4]:text-palette-current-deep">
            <RichText body={articlePage.body} />
          </div>
          {articlePage.nextArticle && (
            <>
              <hr className="w-full h-[1px] text-brand-black/20 my-20" />
              <div className="px-2 dsk:px-11 flex flex-col gap-10 dsk:flex-row justify-between dsk:items-center">
                {!!articlePage.nextUpLabel && (
                  <p className="text-body-large">{articlePage.nextUpLabel}</p>
                )}
                {(() => {
                  const link = readFragment(
                    LinkFragment,
                    articlePage.nextArticle
                  );

                  return (
                    <Link
                      link={link}
                      className="flex items-center gap-6 text-body-large"
                    >
                      <span>{link.text}</span>
                      <Button
                        asChild
                        variant="light-on-true"
                        className="!bg-palette-current-deep aspect-square size-9 max-w-9 !p-0"
                      >
                        <div>
                          <IconArrowRight size="small" />
                        </div>
                      </Button>
                    </Link>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
